/**
 * GlobalSearch — full-platform search modal using Meilisearch.
 * Opens on Ctrl+K / ?K from anywhere in the app.
 */
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useAuthStore, useChatStore } from '../../stores';
import type { TFunction } from 'i18next';
import { intlLocale } from '../../lib/i18nText';

const API = import.meta.env['VITE_API_URL'] ?? '/api';

type SearchIndex = 'messages' | 'files' | 'users' | 'meetings' | 'mentions';

interface SearchHit {
  id: string;
  [key: string]: unknown;
}

const TABS: { key: SearchIndex; label: (t: TFunction) => string }[] = [
  { key: 'messages', label: (t) => t('search.messages', 'Messages') },
  { key: 'mentions', label: (t) => t('searchUi.tabs.mentions', '@Mentions') },
  { key: 'files',    label: (t) => t('search.files', 'Files') },
  { key: 'users',    label: (t) => t('search.people', 'People') },
  { key: 'meetings', label: (t) => t('search.meetings', 'Meetings') },
];

async function fetchSearch(
  q: string,
  index: SearchIndex,
  token: string,
  filter?: string,
  _mentionName?: string,
  mentionedUserId?: string,
): Promise<{ hits: SearchHit[]; totalHits: number; processingTimeMs: number }> {
  if (!q.trim() && index !== 'mentions') return { hits: [], totalHits: 0, processingTimeMs: 0 };

  // Mentions tab: search messages index filtered by mentionedUserId
  const effectiveIndex = index === 'mentions' ? 'messages' : index;
  const effectiveQuery = index === 'mentions'
    ? (q.trim() || '')
    : q;

  if (!effectiveQuery.trim() && !mentionedUserId) return { hits: [], totalHits: 0, processingTimeMs: 0 };

  let url = `${API}/v1/search?q=${encodeURIComponent(effectiveQuery)}&index=${effectiveIndex}&limit=20`;
  if (filter) url += `&filter=${encodeURIComponent(filter)}`;
  if (mentionedUserId) url += `&mentionedUserId=${encodeURIComponent(mentionedUserId)}`;

  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  const json = await res.json();
  return json.data ?? { hits: [], totalHits: 0, processingTimeMs: 0 };
}

interface Props {
  token: string;
  onClose: () => void;
  onOpenDm?: (userId: string, displayName: string) => void;
  /** Pre-filled query, e.g. from the command palette */
  initialQuery?: string;
  /** Opens a chat room, optionally scrolled to a message */
  onOpenMessage?: (roomId: string, messageId?: string) => void;
}

export function GlobalSearch({ token, onClose, onOpenDm, initialQuery = '', onOpenMessage }: Props) {
  const { t }           = useTranslation();
  const { user }        = useAuthStore();
  const [q, setQ]       = useState(initialQuery);
  const [tab, setTab]   = useState<SearchIndex>('messages');
  const inputRef        = useRef<HTMLInputElement>(null);
  const resultsRef      = useRef<HTMLDivElement>(null);

  // -- Keyboard: ↓ from the box into the results, ↑/↓ between them, ↑ from the first back to the box
  const hitButtons = () => Array.from(resultsRef.current?.querySelectorAll<HTMLElement>('[data-testid="search-hit"][tabindex="0"]') ?? []);
  const focusHit = (i: number) => { const hits = hitButtons(); hits[Math.max(0, Math.min(i, hits.length - 1))]?.focus(); };
  const onResultsKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const hits = hitButtons();
    const i = hits.indexOf(document.activeElement as HTMLElement);
    if (i < 0) return;
    e.preventDefault();
    if (e.key === 'ArrowUp' && i === 0) inputRef.current?.focus();
    else focusHit(i + (e.key === 'ArrowDown' ? 1 : -1));
  };

  // -- Filters ----------------------------------------------
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo]     = useState('');
  const [senderFilter, setSenderFilter] = useState('');
  const [showFilters, setShowFilters]   = useState(false);
  const [mentionsOnly, setMentionsOnly] = useState(false);

  // -- Search history ---------------------------------------
  const HISTORY_KEY = 'eurion_search_history';
  const [searchHistory, setSearchHistory] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem(HISTORY_KEY) ?? '[]'); } catch { return []; }
  });

  const saveToHistory = (query: string) => {
    if (!query.trim() || query.length < 2) return;
    const updated = [query, ...searchHistory.filter(h => h !== query)].slice(0, 10);
    setSearchHistory(updated);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(updated));
  };

  // Build filter string for Meilisearch
  const buildFilter = () => {
    const parts: string[] = [];
    if (dateFrom) parts.push(`createdAt >= "${dateFrom}T00:00:00Z"`);
    if (dateTo)   parts.push(`createdAt <= "${dateTo}T23:59:59Z"`);
    if (senderFilter && tab === 'messages') parts.push(`senderName = "${senderFilter}"`);
    return parts.join(' AND ');
  };

  const { data, isLoading } = useQuery({
    queryKey:  ['search', q, tab, dateFrom, dateTo, senderFilter, mentionsOnly, user?.id],
    queryFn:   () => {
      const effectiveTab = (tab === 'messages' && mentionsOnly) ? 'mentions' : tab;
      const mentionedUserId = (effectiveTab === 'mentions' && user?.id) ? user.id : undefined;
      return fetchSearch(
        q,
        effectiveTab,
        token,
        buildFilter(),
        user?.displayName,
        mentionedUserId,
      );
    },
    enabled:   q.length > 1 || tab === 'mentions' || (tab === 'messages' && mentionsOnly),
    staleTime: 10_000,
  });

  const hits = data?.hits ?? [];

  const searchVirtualizer = useVirtualizer({
    count: hits.length,
    getScrollElement: () => resultsRef.current,
    estimateSize: () => 44,
    overscan: 8,
  });

  // Focus input on mount
  useEffect(() => { inputRef.current?.focus(); }, []);

  // Dismiss on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    /* backdrop */
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 backdrop-blur-sm pt-16 animate-fade-in"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-2xl rounded-xl border border-nord-3/40
                   bg-nord-1 shadow-lifted animate-modal-in"
        data-testid="global-search"
      >
        {/* Search input */}
        <div className="flex items-center gap-3 border-b border-nord-3/30 px-4 py-3">
          <svg className="h-5 w-5 shrink-0 text-nord-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={q.trim().length > 0}
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && q.trim()) saveToHistory(q.trim());
              if (e.key === 'ArrowDown') { e.preventDefault(); focusHit(0); }
            }}
            placeholder={t('search.placeholder')}
            className="flex-1 bg-transparent text-nord-6 placeholder-nord-4 outline-none"
            data-testid="search-input"
          />
          <button
            type="button"
            onClick={() => setShowFilters(f => !f)}
            className={[
              'rounded-lg p-1.5 transition-colors',
              showFilters ? 'bg-nord-10/20 text-nord-8' : 'text-nord-4 hover:text-nord-6',
            ].join(' ')}
            title={t('searchUi.filters', 'Filters')}
            aria-label={t('searchUi.filters', 'Filters')}
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
          </button>
          <kbd className="hidden rounded bg-nord-2 px-1.5 py-0.5 font-mono text-2xs text-nord-muted sm:block">
            {'ESC'}
          </kbd>
        </div>

        {/* Filter bar */}
        {showFilters && (
          <div className="flex flex-wrap items-center gap-3 border-b border-nord-3/30 px-4 py-2 bg-nord-0/50">
            <label className="flex items-center gap-1.5 text-xs text-nord-4">
              {t('searchUi.from', 'From')}
              <input
                type="date"
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
                className="rounded border border-nord-3/40 bg-nord-1 px-2 py-0.5 text-xs text-nord-6"
              />
            </label>
            <label className="flex items-center gap-1.5 text-xs text-nord-4">
              {t('searchUi.to', 'To')}
              <input
                type="date"
                value={dateTo}
                onChange={e => setDateTo(e.target.value)}
                className="rounded border border-nord-3/40 bg-nord-1 px-2 py-0.5 text-xs text-nord-6"
              />
            </label>
            {tab === 'messages' && (
              <label className="flex items-center gap-1.5 text-xs text-nord-4">
                {t('searchUi.sender', 'Sender')}
                <input
                  type="text"
                  value={senderFilter}
                  onChange={e => setSenderFilter(e.target.value)}
                  placeholder={t('searchUi.senderPlaceholder', 'Name…')}
                  className="w-28 rounded border border-nord-3/40 bg-nord-1 px-2 py-0.5 text-xs text-nord-6 placeholder-nord-muted"
                />
              </label>
            )}
            {tab === 'messages' && (
              <button
                type="button"
                onClick={() => setMentionsOnly(v => !v)}
                className={[
                  'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors',
                  mentionsOnly
                    ? 'bg-nord-8 text-white'
                    : 'bg-nord-2 text-nord-4 hover:bg-nord-3 hover:text-nord-6',
                ].join(' ')}
              >
                @
                <span>{t('searchUi.mentionsOnly', 'Mentions only')}</span>
              </button>
            )}
            {(dateFrom || dateTo || senderFilter || mentionsOnly) && (
              <button
                type="button"
                onClick={() => { setDateFrom(''); setDateTo(''); setSenderFilter(''); setMentionsOnly(false); }}
                className="text-xs text-nord-8 hover:underline"
              >
                {t('searchUi.clearFilters', 'Clear filters')}
              </button>
            )}
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-nord-3/30 px-4">
          {TABS.map(tab_ => (
            <button
              key={tab_.key}
              type="button"
              onClick={() => setTab(tab_.key)}
              className={[
                'px-3 py-2 text-sm transition-colors',
                tab === tab_.key
                  ? 'border-b-2 border-nord-8 text-nord-6 font-medium'
                  : 'text-nord-4 hover:text-nord-6',
              ].join(' ')}
            >
              {tab_.label(t)}
            </button>
          ))}
        </div>

        {/* Results */}
        <div ref={resultsRef} className="max-h-96 overflow-y-auto p-2" onKeyDown={onResultsKeyDown}>
          {isLoading && (
            <p className="px-3 py-8 text-center text-sm text-nord-4">
              {t('common.loading')}
            </p>
          )}
          {!isLoading && (q.length > 1 || tab === 'mentions') && data?.hits.length === 0 && (
            <p className="px-3 py-8 text-center text-sm text-nord-4">
              {tab === 'mentions' ? t('searchUi.noMentions', 'No mentions found') : t('search.noResults', { query: q })}
            </p>
          )}
          {hits.length > 0 && (
            <div style={{ height: searchVirtualizer.getTotalSize(), position: 'relative' }}>
              {searchVirtualizer.getVirtualItems().map(vItem => {
                const hit = hits[vItem.index]!;
                return (
                  <div
                    key={hit.id}
                    ref={searchVirtualizer.measureElement}
                    data-index={vItem.index}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      transform: `translateY(${vItem.start}px)`,
                    }}
                  >
                    <SearchHitRow
                      hit={hit}
                      index={tab === 'mentions' ? 'mentions' : tab}
                      onOpenDm={tab === 'users' ? onOpenDm : undefined}
                      onOpenMessage={onOpenMessage}
                      onClose={onClose}
                    />
                  </div>
                );
              })}
            </div>
          )}
          {!q && searchHistory.length > 0 && (
            <div className="px-3 py-3">
              <p className="text-2xs font-semibold uppercase tracking-wider text-nord-muted mb-2">{t('searchUi.recent', 'Recent searches')}</p>
              {searchHistory.map((h, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setQ(h)}
                  className="flex items-center gap-2 w-full rounded-lg px-3 py-1.5 text-sm text-nord-6 hover:bg-nord-2/50 transition-colors"
                >
                  <svg className="h-3.5 w-3.5 text-nord-muted shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="truncate">{h}</span>
                </button>
              ))}
              <button
                type="button"
                onClick={() => { setSearchHistory([]); localStorage.removeItem(HISTORY_KEY); }}
                className="mt-1 text-xs text-nord-muted hover:text-nord-8 hover:underline px-3"
              >
                {t('searchUi.clearHistory', 'Clear history')}
              </button>
            </div>
          )}
          {!q && searchHistory.length === 0 && (
            <p className="px-3 py-8 text-center text-sm text-nord-4">
              {t('search.placeholder')}
            </p>
          )}
        </div>

        {/* Footer */}
        {data && data.totalHits > 0 && (
          <div className="border-t border-nord-3/30 px-4 py-2 text-xs text-nord-muted">
            {t('search.results', { count: data.totalHits })} · {data.processingTimeMs}ms
          </div>
        )}
      </div>
    </div>
  );
}

function SearchHitRow({
  hit,
  index,
  onOpenDm,
  onOpenMessage,
  onClose,
}: {
  hit: SearchHit;
  index: SearchIndex;
  onOpenDm?: (userId: string, displayName: string) => void;
  onOpenMessage?: (roomId: string, messageId?: string) => void;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const isMessage = index === 'messages' || index === 'mentions';
  // Show the room's name, not its id
  const roomName = useChatStore(s => {
    if (!isMessage) return null;
    const r = s.rooms.find(x => x.id === hit.roomId);
    return r ? (r.type === 'direct' ? (r.displayName ?? r.name) : `#${r.name}`) : null;
  });

  const getTitle = () => {
    if (index === 'messages' || index === 'mentions') return (hit.content as string)?.slice(0, 100);
    if (index === 'files')    return hit.name as string;
    if (index === 'users')    return (hit.displayName as string) ?? (hit.username as string);
    if (index === 'meetings') return hit.title as string;
    return hit.id;
  };
  const getSub = () => {
    if (index === 'messages' || index === 'mentions') return roomName ? `${hit.senderName} · ${roomName}` : (hit.senderName as string);
    if (index === 'files')    return `${hit.mimeType} · ${hit.sizeBytes ? Math.round((hit.sizeBytes as number) / 1024) + ' KB' : ''}`;
    if (index === 'users')    return hit.email as string;
    if (index === 'meetings') {
      const start = new Date(hit.scheduledStart as string);
      return isNaN(start.getTime())
        ? (hit.scheduledStart as string)
        : start.toLocaleString(intlLocale(i18n?.language), { dateStyle: 'medium', timeStyle: 'short' });
    }
    return '';
  };

  const opensMessage = isMessage && !!onOpenMessage && typeof hit.roomId === 'string';
  const isActionable = (index === 'users' && !!onOpenDm) || opensMessage;

  const handleClick = () => {
    if (!isActionable) return;
    if (opensMessage) {
      onOpenMessage!(hit.roomId as string, hit.id as string);
      onClose();
      return;
    }

    const displayName = (hit.displayName as string) ?? (hit.username as string) ?? t('searchUi.unknown', 'Unknown');
    onOpenDm!(hit.id as string, displayName);
    onClose();
  };

  return (
    <div
      role={isActionable ? 'button' : undefined}
      tabIndex={isActionable ? 0 : undefined}
      onKeyDown={isActionable ? (e) => { if (e.key === 'Enter') handleClick(); } : undefined}
      onClick={isActionable ? handleClick : undefined}
      className={[
        'group flex items-center gap-3 rounded-lg px-3 py-2.5 transition-colors',
        isActionable
          ? 'cursor-pointer hover:bg-nord-10/10 hover:text-nord-8'
          : 'hover:bg-nord-2/40',
      ].join(' ')}
      data-testid="search-hit"
    >
      {/* Avatar for users */}
      {index === 'users' && (
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-nord-10/20 text-xs font-semibold text-nord-8">
          {((hit.displayName as string) ?? (hit.username as string) ?? (hit.email as string) ?? '?').charAt(0).toUpperCase()}
        </div>
      )}

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="truncate text-sm text-nord-6 font-medium">{getTitle()}</p>
        </div>
        <p className="truncate text-xs text-nord-4">{getSub()}</p>
      </div>

      {/* Action hint */}
      {isActionable && (
        <span className="shrink-0 text-xs text-nord-muted opacity-0 group-hover:opacity-100
                         transition-opacity whitespace-nowrap">
          {t('searchUi.messageHint', 'Message →')}
        </span>
      )}
    </div>
  );
}
